import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarCheck, Plus } from "lucide-react";
import { Alert, Button, SegmentedTabs, Skeleton, Typography } from "@akuna/ui";
import { useDaycareCapacity } from "@/hooks/useDaycareCapacity";
import { useOvernightConfig } from "@/hooks/useOvernightConfig";
import { useStayPacks } from "@/hooks/useStayPacks";
import { OccupancyCards } from "../panel/OccupancyCards";
import { PackGroup } from "../panel/PackGroup";
import { PetCard } from "../panel/PetCard";
import { daycareOccupancy, groupPetsByPack, panelPetCardDetailsByPetId, sortPanelPets } from "../panel/panelModel";
import { usePanelDay } from "../panel/usePanelDay";
import { useStayActivities } from "../activities/useStayActivities";
import NewReservationDialog from "../reservation/NewReservationDialog";
import { matchesShowFilter, toIsoDate } from "../schedule/scheduleModel";
import { CheckInDialog } from "../stay/CheckInDialog";
import { FollowUpDialog } from "../stay/FollowUpDialog";
import { OvernightDialog } from "../stay/OvernightDialog";
import type { StayTabProps } from "./types";

type PanelView = "grid" | "packs";

export default function PanelTab({ show = "all" }: StayTabProps) {
  const { t } = useTranslation("daycareHotel");
  const today = useMemo(() => toIsoDate(new Date()), []);
  const [view, setView] = useState<PanelView>("grid");
  const [reservationOpen, setReservationOpen] = useState(false);

  const day = usePanelDay(today);
  const activities = useStayActivities({ day: today, show });
  const packs = useStayPacks();
  const capacity = useDaycareCapacity();
  const overnight = useOvernightConfig();

  const pets = useMemo(
    () => sortPanelPets((day.data ?? []).filter((pet) => matchesShowFilter(pet.nature, show))),
    [day.data, show],
  );
  const occupancy = useMemo(
    () => daycareOccupancy(pets, capacity.data ?? { id: null, totalCapacity: null, limitBySize: false, capacitySmall: null, capacityMedium: null, capacityLarge: null }),
    [pets, capacity.data],
  );
  const groups = useMemo(
    () => groupPetsByPack(pets, packs.data ?? [], today),
    [pets, packs.data, today],
  );
  const cardDetailsByPetId = useMemo(
    () => panelPetCardDetailsByPetId(
      pets,
      packs.data ?? [],
      (activities.data ?? []).map((activity) => ({
        targetType: activity.targetType,
        targetPetId: activity.targetPetId,
        targetPackId: activity.record.targetPackId,
        scheduledTime: activity.record.scheduledTime,
        time: activity.time,
        label: activity.typeName,
        done: activity.done,
      })),
      today,
    ),
    [activities.data, packs.data, pets, today],
  );

  // Guarda só o id: derivar o pet de `pets` a cada render mantém o modal em
  // dia com o que a ação acabou de mudar (hora, pernoite), sem precisar
  // fechar e reabrir para ver o resultado.
  const [checkInReservationId, setCheckInReservationId] = useState<string | null>(null);
  const [overnightReservationId, setOvernightReservationId] = useState<string | null>(null);
  const [followUpReservationId, setFollowUpReservationId] = useState<string | null>(null);
  const overnightEnabled = overnight.data?.isEnabled ?? false;

  const checkInPet = checkInReservationId ? pets.find((pet) => pet.reservationId === checkInReservationId) ?? null : null;
  const overnightPet = overnightReservationId ? pets.find((pet) => pet.reservationId === overnightReservationId) ?? null : null;
  const followUpPet = followUpReservationId ? pets.find((pet) => pet.reservationId === followUpReservationId) ?? null : null;

  if (day.isLoading) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-44 rounded-2xl" label={t("common.loading")} />
        <Skeleton className="h-64 rounded-2xl" label={t("common.loading")} />
      </div>
    );
  }

  if (day.isError) return <Alert variant="danger">{t("panel.loadError")}</Alert>;

  return (
    <div>
      <OccupancyCards occupancy={occupancy} show={show} />

      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2.5">
          <Typography variant="h4">{t("panel.petsToday")}</Typography>
          <Typography variant="body-sm" className="text-muted-foreground">
            {t("panel.petsPresent", { count: pets.length })}
          </Typography>
        </div>
        <SegmentedTabs
          variant="outline-secondary"
          label={t("panel.viewLabel")}
          value={view}
          onValueChange={(next) => setView(next as PanelView)}
          items={[
            { value: "grid", label: t("panel.viewGrid") },
            { value: "packs", label: t("panel.viewPacks") },
          ]}
        />
      </div>

      {pets.length === 0 ? (
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-card px-6 py-14 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <CalendarCheck size={26} className="text-primary" />
          </div>
          <div className="space-y-1">
            <p className="font-display text-[15px] font-semibold text-foreground">{t("panel.emptyTitle")}</p>
            <p className="text-sm text-muted-foreground">{t("panel.emptyDescription")}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setReservationOpen(true)}>
            <Plus size={16} /> {t("newReservation")}
          </Button>
        </div>
      ) : view === "grid" ? (
        <div className="grid gap-4 sm:grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
          {pets.map((pet) => (
            <PetCard
              key={pet.reservationId}
              pet={pet}
              details={cardDetailsByPetId.get(pet.petId)}
              overnightEnabled={overnightEnabled}
              onCheckIn={(pet) => setCheckInReservationId(pet.reservationId)}
              onFollowUp={(pet) => setFollowUpReservationId(pet.reservationId)}
              onOvernight={(pet) => setOvernightReservationId(pet.reservationId)}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <PackGroup
              key={group.id}
              group={group}
              overnightEnabled={overnightEnabled}
              onCheckIn={(pet) => setCheckInReservationId(pet.reservationId)}
              onFollowUp={(pet) => setFollowUpReservationId(pet.reservationId)}
              onOvernight={(pet) => setOvernightReservationId(pet.reservationId)}
            />
          ))}
        </div>
      )}

      {checkInPet && (
        <CheckInDialog
          pet={checkInPet}
          isoDate={today}
          open
          onOpenChange={(next) => { if (!next) setCheckInReservationId(null); }}
        />
      )}
      {overnightPet && (
        <OvernightDialog
          pet={overnightPet}
          isoDate={today}
          open
          onOpenChange={(next) => { if (!next) setOvernightReservationId(null); }}
        />
      )}
      {followUpPet && (
        <FollowUpDialog
          pet={followUpPet}
          isoDate={today}
          open
          onOpenChange={(next) => { if (!next) setFollowUpReservationId(null); }}
        />
      )}

      <NewReservationDialog open={reservationOpen} onOpenChange={setReservationOpen} />
    </div>
  );
}
